<template>
  <div>
    <aw-product-list
      :is-whole-loading-completed="isWholeLoadingCompleted"
      :is-more-loading-completed="isMoreLoadingCompleted"
      :item-count="itemCount"
      @load-more-product-items="loadMoreProductItems"
      @order-product-items="orderProductItems"
      @filter-product-items="filterProductItems"
    >
      <template #content>
        <aw-product-list-header v-if="itemCount" :product-counter="{ all: itemCount, loaded: products.length }">
          <template #listName>
            <lv-heading
              v-if="shortlist"
              tag="h1"
              level="2"
              version2
              no-gutter
            >
              {{ shortlist.title }}
            </lv-heading>
            <p :class="$style.headline" v-html="shortlist.headline" />
          </template>
          <template #filters>
            <aw-subcategory-filter
              v-if="screenRange['md-max']"
              view="FULL"
              @activate-filter="filterProductItems()"
            />
          </template>
        </aw-product-list-header>
      </template>
    </aw-product-list>
    <aw-content-seo-text
      v-if="getCategoryFromTree && getCategoryFromTree.seoTextId"
      :content-id="getCategoryFromTree.seoTextId"
    />
  </div>
</template>

<script>
  import { defineAsyncComponent } from 'vue';
  import { mapState } from 'pinia';
  import { defineNuxtComponent, useRoute } from 'nuxt/app';
  import { fetchKey } from '~~/common/utils/awRunLegacyAsyncData.js';
  import { LvHeading } from '~~/common/components/loginet-vue-shop/index.mjs';
  import AwProductList from '~~/common/components/Page/ProductList/AwProductList';
  import AwProductListHeader from '~~/common/components/Page/ProductList/AwProductListHeader.vue';
  import productListHistoryMixin from '~~/common/mixins/productListHistoryMixin';

  import { useContentsStore } from '~~/common/stores/contents';
  import { useUserInterfaceStore } from '~~/common/stores/userInterface';
  import { useCategoryStore } from '~~/common/stores/category';
  import { useProductsStore } from '~~/common/stores/products';
  import { useDeliveryStore } from '~~/shop/stores/delivery';
  import { usePromotionStore } from '~~/shop/stores/promotion';

  export default defineNuxtComponent({
    name: 'AwShortlistPage',
    components: {
      AwProductList,
      AwProductListHeader,
      AwSubcategoryFilter: defineAsyncComponent(() => import('~~/common/components/Page/ProductList/AwSubcategoryFilter.vue')),
      AwContentSeoText: defineAsyncComponent(() => import('~~/shop/components/AwContentSeoText')),
      LvHeading,
    },
    mixins: [
      productListHistoryMixin,
    ],
    fetchKey,
    async asyncData ({
      $logger,
      $pinia,
    }) {
      const route = useRoute();
      const categoryStore = useCategoryStore($pinia);
      const productsStore = useProductsStore($pinia);
      const promotionStore = usePromotionStore($pinia);
      const contentsStore = useContentsStore($pinia);
      try {
        const shortlistReq = promotionStore.fetchShortlists({
          identifier: route.params.list_name,
          type: route.query.shType || 'slug',
        }).then(async (shortlistId) => {
          try {
            const {
              type,
              id,
            } = categoryStore.extractPromotionContentFromObject(promotionStore.shortlists[shortlistId]);
            if (type === 'banner') {
              await promotionStore.fetchBanner({ id });
            }
          } catch (error) {
            if (error?.response?.status !== 404) {
              $logger.error(error);
            }
          }
          return shortlistId;
        });

        const isReturningFromProductPage = productsStore.productListHistoryInformation?.location?.includes(route.fullPath);
        const isReturningEnabled = productsStore.productListHistoryInformation?.returnEnabled;

        const treeId = parseInt(route.query.categoryId, 10) || null;
        const boutiqueId = parseInt(route.query.boutiqueId, 10) || null;

        if (isReturningFromProductPage && isReturningEnabled) {
          productsStore.restoreFromProductListHistoryInformation();
        } else {
          await productsStore.fetchProducts({
            [route.query.shType === 'id' ? 'listId' : 'listName']: boutiqueId ? null : route.params.list_name,
            isCached: true,
            boutiqueId,
            itemsPerPage: productsStore.itemsPerPage || 16,
            page: 1,
          });
        }

        const shortlistId = await shortlistReq;
        await contentsStore.fetchStaticContentByResourceId({
          type: 'shortlist_content',
          resourceId: shortlistId,
        });

        return {
          shortlistId,
          treeId,
          boutiqueId,
        };
      } catch (error) {
        if (error?.response?.status !== 404) {
          $logger.error(error);
        }
        return {
          shortlistId: null,
          treeId: null,
          boutiqueId: null,
        };
      }
    },
    data () {
      return {
        isWholeLoadingCompleted: true,
        isMoreLoadingCompleted: true,
      };
    },
    computed: {
      ...mapState(useCategoryStore, {
        getCategoryFromTree: state => state.getCategoryFromTree,
        promotionContent (state) {
          return state.extractPromotionContentFromObject(this.shortlist);
        },
      }),
      ...mapState(usePromotionStore, {
        shortlist (state) {
          return state.shortlists[this.shortlistId];
        },
      }),
      ...mapState(useUserInterfaceStore, {
        screenRange: state => state.mediaQueries,
      }),
      ...mapState(useProductsStore, {
        itemsPerPage: state => state.itemsPerPage,
        currentPage: state => state.currentPage,
        itemCount: state => state.itemCount,
        sortKey: state => state.sortKey,
        sortBy: state => state.sortBy,
        filterBy: state => state.filterBy,
        products: state => state.renderedProducts,
      }),
      ...mapState(useDeliveryStore, {
        deliverySetup: state => state.setup,
      }),
      promotionRecommendationCarouselId () {
        return this.shortlist?.promotionRecommendationCarouselId;
      },
      productBaseQuery () {
        const data = {
          isCached: true,
          listId: this.boutiqueId ? null : this.shortlistId,
          boutiqueId: this.boutiqueId,
          itemsPerPage: this.itemsPerPage,
          page: this.currentPage,
          filterParams: this.filterBy,
        };
        if (this.sortKey) {
          data.sortKey = this.sortKey;
        }
        if (this.sortBy) {
          data.sortBy = this.sortBy;
        }
        return data;
      },
    },
    watch: {
      deliverySetup () {
        this.$nuxt.refresh();
      },
    },
    mounted () {
      const categoryStore = useCategoryStore();
      const productsStore = useProductsStore();
      categoryStore.setTreeId(this.treeId);
      if (this.isReturningFromProductPage) {
        const productId = productsStore.productListHistoryInformation?.productId;
        this.scrollToProductId(productId);
      }
    },
    methods: {
      async loadMoreProductItems (loadingState) {
        const productsStore = useProductsStore();
        this.isMoreLoadingCompleted = false;

        if (loadingState && this.itemCount === this.products.length) {
          loadingState.complete();
          return;
        }

        const result = await productsStore.fetchProducts({
          ...this.productBaseQuery,
          page: this.productBaseQuery.page + 1,
        });

        this.isMoreLoadingCompleted = true;
        if (loadingState) {
          if (!result || this.itemCount === this.products.length) {
            loadingState.complete();
          } else {
            loadingState.loaded();
          }
        }
      },
      async orderProductItems (orderParams) {
        const productsStore = useProductsStore();
        this.isMoreLoadingCompleted = false;
        let data = {
          ...this.productBaseQuery,
          itemsPerPage: this.currentPage * this.itemsPerPage,
          page: 1,
        };

        if (this.sortKey) {
          delete data.sortKey;
        }
        if (this.sortBy) {
          delete data.sortBy;
        }

        data = {
          ...data,
          ...orderParams.value,
        };

        await productsStore.fetchProducts(data);
        this.isMoreLoadingCompleted = true;
      },
      async filterProductItems () {
        const productsStore = useProductsStore();
        this.isMoreLoadingCompleted = false;
        await productsStore.fetchProducts({
          ...this.productBaseQuery,
          itemsPerPage: this.currentPage * this.itemsPerPage,
          page: 1,
        });
        this.isMoreLoadingCompleted = true;
      },
    },
  });
</script>

<style module lang="scss" rel="stylesheet/scss">
.promotion {
  @include lg() {
    margin-bottom: 27px;
  }
  margin: 23px 0 18px;
}

.headline {
  margin-top: 12px;
  margin-bottom: 20px;
}
</style>
